import { FC, useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { Box, Button, Modal, Typography } from '@mui/material';
import { convertsCentsToCurrency } from '@npi/buddy-utils';
import { Formik } from 'formik';
import { BsFillEyeFill } from 'react-icons/bs';

import { Flex } from 'src/components/Flex';
import RiskLevels from 'src/components/RiskLevels/RiskLevels';
import { showErrorToast } from 'src/components/Toasts/toast';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { GET_PARTNER } from 'src/graphql/queries/partners.query';
import { GET_PRICING_OFFER } from 'src/graphql/queries/pricing.query';
import { useTransactionPricing } from 'src/hooks';
import useGeneratePresignedUrl from 'src/hooks/useGeneratePresignedUrl';
import { TransactionCharges } from 'src/pages/AddMerchantPage/components/TransactionCharges';
import { getMinimumContractTerm } from 'src/utils/utils';

import { CardSection } from '../CardSection';
import { TextDisplay } from '../TextDisplay';

interface ServiceRequirementsInformation {}

const ServiceRequirementsInformation: FC<ServiceRequirementsInformation> = () => {
    const { merchantApplication } = useAddMerchantContext();
    const [isTransactionVisible, setIsTransactionVisible] = useState(false);

    const { activeTransactionPricingOffers } = useTransactionPricing();

    const foundOffer = activeTransactionPricingOffers?.filter((transaction) => {
        return (
            transaction.id ===
            Number(merchantApplication?.serviceRequirements?.service?.transactionPricingOfferId)
        );
    });

    const transactionPricingOfferName = foundOffer.map((offer) => {
        return offer.offerName;
    });

    const primaryProducts = useMemo(() => {
        return merchantApplication?.serviceRequirements?.order?.primary ?? [];
    }, [merchantApplication]);
    const secondaryProducts = useMemo(() => {
        return merchantApplication?.serviceRequirements?.order?.secondary ?? [];
    }, [merchantApplication]);

    const contractTermInMonths = getMinimumContractTerm(merchantApplication?.serviceRequirements);
    const contractTerm = useMemo(() => {
        return merchantApplication?.serviceRequirements?.service?.serviceType ?? '';
    }, [merchantApplication]);

    const selectedPricingOffer =
        merchantApplication?.serviceRequirements?.service?.selectedTerminalOffer ?? '';

    const [getPartner, { data }] = useLazyQuery(GET_PARTNER, {
        onError: (err) => {
            return showErrorToast(err.message);
        },
    });

    const [getPricingOffer, { data: pricingOfferData }] = useLazyQuery(GET_PRICING_OFFER, {
        onError: (err) => {
            return showErrorToast(err.message);
        },
    });

    useEffect(() => {
        getPricingOffer({ variables: { id: parseInt(selectedPricingOffer) } });
    }, [selectedPricingOffer]);

    useEffect(() => {
        const partnerId = merchantApplication?.partnerId;
        if (partnerId) {
            getPartner({ variables: { id: partnerId } });
        }
    }, []);

    const partnerName = useMemo(() => {
        const legalPartnerName = data?.partner?.business?.legalName;

        if (legalPartnerName) {
            return legalPartnerName;
        }
        return null;
    }, [data]);

    const [imageUrls, setImageUrls] = useState({});
    const { validateAwsURL } = useGeneratePresignedUrl();
    const products = useMemo(() => {
        return [...primaryProducts, ...secondaryProducts];
    }, [primaryProducts, secondaryProducts]);

    useEffect(() => {
        const fetchPresignedUrls = async () => {
            const updatedUrls = {};

            const productsToFetch = products.filter((product) => {
                return !imageUrls[product.id];
            });

            if (productsToFetch.length === 0) return;

            for (const product of productsToFetch) {
                const presignedUrl = await validateAwsURL(product.imageUrl);
                updatedUrls[product.id] = presignedUrl ?? product.imageUrl;
            }

            if (Object.keys(updatedUrls).length > 0) {
                setImageUrls((prev) => {
                    return { ...prev, ...updatedUrls };
                });
            }
        };

        fetchPresignedUrls();
    }, [products, imageUrls, validateAwsURL]);

    return (
        <Flex flexDirection="column" gap="24px">
            <CardSection heading="Merchant Category Code/Turnover GPV">
                <Flex flexDirection="column" gap="24px">
                    <Flex>
                        <TextDisplay
                            label="Merchant Category Code"
                            value={
                                merchantApplication?.serviceRequirements?.salesConditions
                                    ?.merchantCategoryCode?.code ?? ''
                            }
                        />
                        <TextDisplay
                            label="Supporting Document"
                            value={
                                merchantApplication?.serviceRequirements?.salesConditions
                                    ?.merchantCategoryCode?.documents ?? 'N/A'
                            }
                        />
                    </Flex>
                    <Flex>
                        <TextDisplay
                            label="Risk Classification"
                            value={
                                <RiskLevels
                                    level={
                                        merchantApplication?.serviceRequirements?.salesConditions
                                            ?.merchantCategoryCode?.risk ?? 'NONE'
                                    }
                                    containerStyles={{ maxWidth: '70%' }}
                                />
                            }
                        />
                        <TextDisplay
                            label="Monthly Turnover/GPV"
                            value={`\u20AC ${
                                merchantApplication?.serviceRequirements?.salesConditions
                                    ?.monthlyTurnover
                                    ? convertsCentsToCurrency(
                                          merchantApplication?.serviceRequirements?.salesConditions
                                              ?.monthlyTurnover
                                      )
                                    : 0
                            }`}
                        />
                    </Flex>
                </Flex>
            </CardSection>
            <CardSection heading="Service Type">
                <Flex flexDirection="column" gap="24px">
                    <Flex>
                        <TextDisplay
                            label="Service Type"
                            value={
                                merchantApplication?.serviceRequirements?.service?.serviceType ?? ''
                            }
                        />
                        <TextDisplay label="Contract Term" value={contractTermInMonths} />
                    </Flex>
                    <Flex>
                        <TextDisplay
                            label={`${
                                contractTerm === 'TERMINAL' ? 'Terminal' : 'Online'
                            } Pricing Offer`}
                            value={pricingOfferData?.offer?.name || ''}
                        />
                        <TextDisplay
                            label="Service Transaction Pricing Offer"
                            value={transactionPricingOfferName[0]}
                        />
                    </Flex>
                    <Box height="1px" width="100%" bgcolor="#8E8E8D" />
                    <Flex>
                        <TextDisplay
                            label="Total One off"
                            value={`\u20AC ${
                                convertsCentsToCurrency(
                                    merchantApplication?.serviceRequirements?.totals?.totalOneOff
                                ) ?? '0'
                            }`}
                        />
                        <TextDisplay
                            label="Total Monthly"
                            value={`\u20AC ${
                                convertsCentsToCurrency(
                                    merchantApplication?.serviceRequirements?.totals?.totalMonthly
                                ) ?? '0'
                            }`}
                        />
                    </Flex>
                    <TextDisplay
                        label="Total Term"
                        value={`\u20AC ${
                            convertsCentsToCurrency(
                                merchantApplication?.serviceRequirements?.totals?.totalTerm
                            ) ?? '0'
                        }`}
                    />
                </Flex>
            </CardSection>
            <CardSection heading="Orders">
                <Flex flexDirection="column" gap="24px">
                    <Box>
                        <TextDisplay label="Primary Order" value="" />
                        <Box>
                            {primaryProducts?.length ? (
                                primaryProducts.map((product, index) => {
                                    return (
                                        <Flex
                                            key={index}
                                            display="flex"
                                            flexDirection="row"
                                            alignContent="center"
                                            gap="25px"
                                        >
                                            <Typography style={{ marginTop: '30px' }}>
                                                {product?.name}
                                            </Typography>
                                            <img
                                                src={imageUrls[product.id] ?? product.imageUrl}
                                                alt={`Product ${product.name}`}
                                                style={{ width: '90px', height: '90px' }}
                                            />
                                        </Flex>
                                    );
                                })
                            ) : (
                                <Typography>No primary order(s)</Typography>
                            )}
                        </Box>
                    </Box>

                    <Box>
                        <TextDisplay label="Secondary Order" value="" />
                        <Box>
                            {secondaryProducts?.length ? (
                                secondaryProducts.map((product, index) => {
                                    return (
                                        <Flex
                                            key={index}
                                            display="flex"
                                            flexDirection="row"
                                            alignContent="center"
                                            gap="25px"
                                        >
                                            <Typography style={{ marginTop: '30px' }}>
                                                {product?.name}
                                            </Typography>
                                            <img
                                                src={imageUrls[product.id] ?? product?.imageUrl}
                                                alt={`Product ${product.name}`}
                                                style={{ width: '90px', height: '90px' }}
                                            />
                                        </Flex>
                                    );
                                })
                            ) : (
                                <Typography>No secondary order(s)</Typography>
                            )}
                        </Box>
                    </Box>
                </Flex>
            </CardSection>
            <CardSection heading="Transaction Pricing">
                <Flex alignItems="flex-end">
                    <TextDisplay
                        label="Service Transaction Pricing offer"
                        value={transactionPricingOfferName[0]}
                    />

                    <Button
                        startIcon={<BsFillEyeFill />}
                        variant="npi-primary"
                        onClick={() => {
                            return setIsTransactionVisible(true);
                        }}
                        sx={{ height: '40px' }}
                    >
                        View
                    </Button>
                </Flex>
            </CardSection>

            {isTransactionVisible && (
                <Formik onSubmit={() => {}} initialValues={merchantApplication.serviceRequirements}>
                    {() => {
                        return (
                            <Modal
                                open={isTransactionVisible}
                                onClose={() => {
                                    return setIsTransactionVisible(false);
                                }}
                            >
                                <Box maxHeight="90vh" width="90vw" margin="30px auto">
                                    <Flex height="100%" sx={{ overflowY: 'scroll' }}>
                                        <TransactionCharges
                                            name="transactionCharges"
                                            editTransactionCharges={false}
                                            hideEditPen
                                            onClose={() => {
                                                return setIsTransactionVisible(false);
                                            }}
                                            serviceType={
                                                merchantApplication?.serviceRequirements?.service
                                                    ?.serviceType
                                            }
                                        />
                                    </Flex>
                                </Box>
                            </Modal>
                        );
                    }}
                </Formik>
            )}
        </Flex>
    );
};
export default ServiceRequirementsInformation;
